import { useMemo } from 'react';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  PaginationOptionsWithSortArray,
  useVecotorContracts,
  VectorApiContractStatus,
  VectorContractDto,
} from '@sib/api';
import { PDF_EXTENSION } from '@sib/config';
import { useNotification } from '@sib/notifications';
import {
  Button,
  mapColumnToColumnWithMinSize,
  TableBadge,
  Tooltip,
} from '@sib/ui';
import { Search, Download } from '@sib/ui/icons';
import { NO_DATA_PLACEHOLDER } from '@spendbrain/config/constants';
import { useDownloadBlobFile } from '@spendbrain/hooks';
import { DOCUMENTS_DATE_FORMAT } from '@spendbrain/modules/documents/utils';
import { useAppTranslation } from '@spendbrain/modules/vendors/hooks';
import { format } from 'date-fns';

enum IsVetted {
  Yes = 'true',
  No = 'false',
}

export interface ContractPreview {
  fileUrl: string;
  title: string;
}

interface UseContractsProps extends PaginationOptionsWithSortArray {
  organizationId: string;
  vendorId: string;
}

export const useContracts = ({
  organizationId,
  vendorId,
  filter,
  sort,
  page,
  size,
}: UseContractsProps) => {
  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    refetch: refetchContracts,
  } = useVecotorContracts(organizationId, vendorId, {
    page,
    size,
    sort,
    filter,
  });

  return {
    refetchContracts,
    data: data?.items || [],
    isLoading: isLoading || isFetching || isRefetching,
    rowCount: data?.totalItems || 0,
  };
};

interface useContractsColumnsProps {
  setContractPreview: (contractPreview: ContractPreview) => void;
}

export const useContractsColumns = ({
  setContractPreview,
}: useContractsColumnsProps) => {
  const { t } = useAppTranslation();
  const { open } = useNotification();

  const columnHelper = createColumnHelper<VectorContractDto>();
  const { downloadBlobFile } = useDownloadBlobFile({
    onError: () => {
      open({
        type: 'error',
        message: t('components.contracts.pdfModal.errorMessage'),
        description: t('components.contracts.pdfModal.errorDescription'),
      });
    },
  });
  const isVettedOptions = useContractIsVettedOptions();
  const contractStatusOptions = useContractStatusOptions();

  const columns = useMemo(
    () =>
      (
        [
          columnHelper.accessor('id', {
            header: t('components.contracts.columns.contractId'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => {
              const contractId = info.getValue() || NO_DATA_PLACEHOLDER;

              return (
                <span className="text-primary font-bold">{contractId}</span>
              );
            },
          }),
          columnHelper.accessor('negotiationNo', {
            header: t('components.contracts.columns.negotationNumber'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
          }),
          columnHelper.accessor('locationName', {
            header: t('components.contracts.columns.propertyName'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
          }),
          columnHelper.accessor('vendorName', {
            header: t('components.contracts.columns.vendorName'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'text',
              },
            },
            cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
          }),
          columnHelper.accessor('isVetted', {
            header: t('components.contracts.columns.isVetted'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'single_select',
                options: isVettedOptions,
              },
            },
            cell: info => {
              const value = info.getValue();
              if (value === undefined) return NO_DATA_PLACEHOLDER;

              return value ? t('yes') : t('no');
            },
          }),
          columnHelper.accessor('effectiveDate', {
            header: t('components.contracts.columns.effectiveDate'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'date',
              },
            },
            cell: info => {
              const value = info.getValue();
              if (!value) return NO_DATA_PLACEHOLDER;

              return format(value, DOCUMENTS_DATE_FORMAT);
            },
          }),
          columnHelper.accessor('status', {
            header: t('components.contracts.columns.contractStatus'),
            size: 150,
            filter: {
              FilterProps: {
                type: 'single_select',
                options: contractStatusOptions,
              },
            },
            cell: info => {
              const value = info.getValue();
              if (!value) return NO_DATA_PLACEHOLDER;
              const contractStatus = contractStatusOptions.find(
                option => option.value === value
              );

              return <TableBadge>{contractStatus?.label || value}</TableBadge>;
            },
          }),
          {
            id: 'actions',
            enableResizing: false,
            size: 90,
            sortable: false,
            pinnable: false,
            header: () => (
              <span className="mb-8 flex w-full items-start justify-center">
                {t('more')}
              </span>
            ),
            cell: info => {
              const hasfileUrl = info.row.original.fileUrl;

              return (
                <Tooltip
                  content={
                    !hasfileUrl &&
                    t('components.contracts.pdfModal.disabledTooltip')
                  }
                  TooltipContentProps={{ align: 'end' }}
                  TooltipTriggerProps={{ asChild: true }}
                >
                  <div className="flex items-center justify-end gap-2 pr-2">
                    <Button
                      variant="ghost"
                      disabled={!hasfileUrl}
                      className="text-primary mt-0.5 size-5.5 p-0"
                      onClick={() =>
                        setContractPreview({
                          fileUrl: info.row.original.fileUrl,
                          title: info.row.original.negotiationNo,
                        })
                      }
                    >
                      <Search />
                    </Button>

                    <Button
                      variant="ghost"
                      disabled={!hasfileUrl}
                      className="text-primary size-6 p-0"
                      onClick={() =>
                        downloadBlobFile(
                          info.row.original.fileUrl,
                          `${info.row.original.negotiationNo}${PDF_EXTENSION}`
                        )
                      }
                    >
                      <Download />
                    </Button>
                  </div>
                </Tooltip>
              );
            },
          },
        ] as AccessorKeyColumnDef<VectorContractDto>[]
      ).map(mapColumnToColumnWithMinSize),
    [
      columnHelper,
      t,
      isVettedOptions,
      contractStatusOptions,
      setContractPreview,
      downloadBlobFile,
    ]
  );

  return columns;
};

const useContractIsVettedOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      { value: IsVetted.Yes, label: t('yes') },
      {
        value: IsVetted.No,
        label: t('no'),
      },
    ],
    [t]
  );
};

const useContractStatusOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      {
        value: VectorApiContractStatus.BillProcessingOnly,
        label: t('components.contracts.contractStatus.billProcessingOnly'),
      },
      {
        value: VectorApiContractStatus.FullyExecuted,
        label: t('components.contracts.contractStatus.fullyExecuted'),
      },
      {
        value: VectorApiContractStatus.Archived,
        label: t('components.contracts.contractStatus.archived'),
      },
    ],
    [t]
  );
};
